// View + set a company location's CUSTOMS CREDENTIAL — the government ID Shopify requires on orders
// shipping to certain countries (e.g. China's "Resident ID number"). Without it Shopify refuses to
// confirm the order, which blocks the warehouse.
//
// The stored value is never sent to the browser: we only ever show its last 4 digits. The input starts
// empty; typing a new value replaces the stored one, and a blank submit changes nothing.
import { useEffect, useState } from 'react';
import { fetchShippingCredential, setShippingCredential } from '../api/companies.js';

const errMsg = (e) => e.response?.data?.error?.message || 'Something went wrong.';

export default function ShippingCredentialCard({ scope, readOnly = false }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [warning, setWarning] = useState('');

  const scopeKey = `${scope?.store}:${scope?.companyLocationId}`;

  const load = () => {
    setLoading(true);
    setError('');
    fetchShippingCredential(scope.store, scope.companyLocationId)
      .then((d) => {
        setState(d);
        setWarning('');
      })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [scopeKey]);

  const save = async (clear = false) => {
    const raw = value.trim();
    if (!clear && !raw) return; // blank submit = no change
    setSaving(true);
    setError('');
    setNotice('');
    setWarning('');
    try {
      const key = state?.key || state?.suggestedKey || null;
      const res = await setShippingCredential({
        companyLocationId: scope.companyLocationId,
        key: clear ? key : key,
        value: clear ? null : raw,
      });
      setState((s) => ({ ...s, ...res }));
      setValue('');
      setNotice(clear ? 'Credential cleared.' : 'Saved — future orders for this location will include it.');
      if (res.warning) setWarning(res.warning);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  // Nothing required for this destination and nothing stored → don't clutter the page.
  if (!state?.required && !state?.hasValue) return null;

  const label = state.label || state.suggestedKey || 'Customs ID';
  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-primary">Customs ID — {label}</h3>
        {state.hasValue ? (
          <span className="rounded-full bg-success-light px-2 py-0.5 text-[11px] font-medium text-success">On file</span>
        ) : (
          <span className="rounded-full bg-error-light px-2 py-0.5 text-[11px] font-medium text-error">Required</span>
        )}
      </div>
      <p className="mb-3 text-xs text-text-muted">
        {state.country
          ? `Shopify requires this for ${state.country} shipments — orders can't be sent to the warehouse without it.`
          : "Shopify requires this for this location's destination country."}
      </p>

      {state.hasValue && (
        <p className="mb-3 text-sm text-text-secondary">
          On file: <span className="font-mono">•••• {state.last4}</span>
          {state.updatedAt ? <span className="text-text-muted"> · updated {new Date(state.updatedAt).toLocaleDateString()}</span> : null}
        </p>
      )}

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="password"
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={state.hasValue ? `Enter a new number to replace •••• ${state.last4}` : 'Enter the number'}
            className="min-w-[16rem] flex-1 rounded-md border border-line bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
          />
          <button
            type="button"
            onClick={() => save(false)}
            disabled={saving || !value.trim()}
            className="rounded-md bg-accent px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {state.hasValue && (
            <button
              type="button"
              onClick={() => save(true)}
              disabled={saving}
              className="rounded-md border border-line px-3 py-2 text-sm text-text-secondary hover:bg-surface-raised disabled:opacity-50"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {notice && <p className="mt-2 text-xs text-success">{notice}</p>}
      {warning && <p className="mt-2 rounded-md border border-warning/40 bg-warning-light px-3 py-2 text-xs text-warning">{warning}</p>}
      {error && <p className="mt-2 text-xs text-error">{error}</p>}
    </div>
  );
}
